import { useState } from "react";
import { toast } from "sonner";
import { Plus, X, Wand2 } from "lucide-react";
import { DAY_LABELS, generateSlots, overlapping, sortSlots } from "@/lib/slots";

const inputClass = "w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-black min-h-[44px]";

/**
 * When a service can be booked (part of the service form, Tenant Admin → Services).
 *
 *   value    { slots, days, available_from, available_until, capacity } (see backend/scheduling.py)
 *   onChange (value) => void
 *   duration the service's duration in minutes: the first suggestion for the slot length
 *   generalSlots the business's general slots, shown when the service uses those
 *
 * Nothing is saved here: the service form saves it, and the server checks it
 * again (overlaps, time ranges, dates).
 */
export default function ServiceScheduleEditor({ value, onChange, duration = 60, generalSlots = [] }) {
  // The service's own list is kept while "general slots" is chosen, so switching back does not lose it.
  const [own, setOwn] = useState(value.slots || []);
  const [mode, setMode] = useState(value.slots?.length ? "own" : "general");
  const [gen, setGen] = useState({ start: value.slots?.[0]?.slice(0, 5) || "09:00", end: value.slots?.[value.slots.length - 1]?.slice(6) || "18:00", minutes: duration || 60, gap: 0 });
  const [one, setOne] = useState({ from: "", to: "" });

  const set = (patch) => onChange({ ...value, ...patch });
  const setSlots = (slots) => { const sorted = sortSlots(slots); setOwn(sorted); set({ slots: sorted }); };
  const choose = (next) => { setMode(next); set({ slots: next === "own" ? own : [] }); };

  const generate = () => {
    if (!gen.start || !gen.end || gen.start >= gen.end) { toast.error("The end time must be after the start time"); return; }
    if (!(gen.minutes >= 5)) { toast.error("A slot must be at least 5 minutes long"); return; }
    const slots = generateSlots(gen.start, gen.end, Number(gen.minutes), Number(gen.gap) || 0);
    if (!slots.length) { toast.error("No slot of that length fits between those times"); return; }
    setSlots(slots);
    toast.success(`${slots.length} slot${slots.length === 1 ? "" : "s"} made. Remove any you do not offer, then save the service.`);
  };

  const addOne = () => {
    if (!one.from || !one.to) { toast.error("Choose a start and an end time"); return; }
    if (one.from >= one.to) { toast.error("The end time must be after the start time"); return; }
    const slot = `${one.from}-${one.to}`;
    if (own.includes(slot)) { toast.error("That time slot is already there"); return; }
    const clash = overlapping(own, slot);
    if (clash) { toast.error(`${slot} overlaps ${clash}. Time slots must not overlap.`); return; }
    if (own.length >= 48) { toast.error("At most 48 time slots"); return; }
    setSlots([...own, slot]);
    setOne({ from: "", to: "" });
  };

  const toggleDay = (d) => {
    const days = value.days.includes(d) ? value.days.filter((x) => x !== d) : [...value.days, d].sort();
    if (!days.length) { toast.error("The service must be bookable on at least one day"); return; }
    set({ days });
  };

  return (
    <div className="border-t border-neutral-200 pt-4 space-y-5" data-testid="service-schedule">
      <div>
        <div className="admin-overline">Booking slots</div>
        <p className="text-xs text-neutral-500 mt-1">The times a customer can book this service. Customers only see slots that are still free.</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {[["general", "The business's general slots", "As set in Profile → Booking time slots"], ["own", "This service's own slots", "Its own start and end times and slot length"]].map(([id, title, hint]) => (
            <label key={id} className={`flex items-start gap-3 border p-3 cursor-pointer min-h-[44px] ${mode === id ? "border-black bg-neutral-50" : "border-neutral-300"}`}>
              <input type="radio" name="slot-mode" checked={mode === id} onChange={() => choose(id)} className="mt-1" data-testid={`service-schedule-mode-${id}`} />
              <span className="min-w-0"><span className="block text-sm font-semibold">{title}</span><span className="block text-xs text-neutral-500">{hint}</span></span>
            </label>
          ))}
        </div>
      </div>

      {mode === "general" && (
        <div className="flex flex-wrap gap-2" data-testid="service-schedule-general">
          {generalSlots.map((slot) => <span key={slot} className="border border-neutral-200 bg-neutral-50 px-2.5 py-1.5 text-xs font-mono text-neutral-600">{slot}</span>)}
        </div>
      )}

      {mode === "own" && (
        <div className="space-y-4" data-testid="service-schedule-own">
          <div className="bg-neutral-50 border border-neutral-200 p-3">
            <div className="text-xs font-semibold">Make the slots from a working day</div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-2 items-end">
              <div className="min-w-0"><label className="admin-overline" htmlFor="gen-start">Start time</label>
                <input id="gen-start" type="time" value={gen.start} onChange={(e) => setGen({ ...gen, start: e.target.value })} className={inputClass} data-testid="service-schedule-start" /></div>
              <div className="min-w-0"><label className="admin-overline" htmlFor="gen-end">End time</label>
                <input id="gen-end" type="time" value={gen.end} onChange={(e) => setGen({ ...gen, end: e.target.value })} className={inputClass} data-testid="service-schedule-end" /></div>
              <div className="min-w-0"><label className="admin-overline" htmlFor="gen-minutes">Slot length (min)</label>
                <input id="gen-minutes" type="number" min="5" max="720" step="5" value={gen.minutes} onChange={(e) => setGen({ ...gen, minutes: e.target.value === "" ? "" : parseInt(e.target.value, 10) })} className={inputClass} data-testid="service-schedule-minutes" /></div>
              <div className="min-w-0"><label className="admin-overline" htmlFor="gen-gap">Break between (min)</label>
                <input id="gen-gap" type="number" min="0" max="240" step="5" value={gen.gap} onChange={(e) => setGen({ ...gen, gap: e.target.value === "" ? "" : parseInt(e.target.value, 10) })} className={inputClass} data-testid="service-schedule-gap" /></div>
              <button type="button" onClick={generate} className="col-span-2 lg:col-span-1 bg-black text-white px-4 text-xs font-semibold uppercase tracking-wider min-h-[44px] inline-flex items-center justify-center gap-1.5" data-testid="service-schedule-generate">
                <Wand2 className="h-4 w-4" /> Make slots
              </button>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold">{own.length ? `${own.length} slot${own.length === 1 ? "" : "s"}` : "No slots yet. Make them above, or add them one by one."}</div>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="service-schedule-slots">
              {own.map((slot) => (
                <span key={slot} className="inline-flex items-center gap-1 border border-neutral-300 bg-white pl-3 pr-1 text-sm font-mono min-h-[44px]" data-testid={`service-slot-${slot}`}>
                  {slot}
                  <button type="button" onClick={() => setSlots(own.filter((s) => s !== slot))} aria-label={`Remove ${slot}`}
                    className="h-9 w-9 inline-flex items-center justify-center text-neutral-500 hover:text-black" data-testid={`service-slot-remove-${slot}`}>
                    <X className="h-4 w-4" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex flex-wrap items-end gap-3 mt-3">
              <div className="min-w-0"><label className="admin-overline" htmlFor="one-from">From</label>
                <input id="one-from" type="time" value={one.from} onChange={(e) => setOne({ ...one, from: e.target.value })} className={inputClass} data-testid="service-slot-from" /></div>
              <div className="min-w-0"><label className="admin-overline" htmlFor="one-to">To</label>
                <input id="one-to" type="time" value={one.to} onChange={(e) => setOne({ ...one, to: e.target.value })} className={inputClass} data-testid="service-slot-to" /></div>
              <button type="button" onClick={addOne} className="border border-black px-4 text-xs font-semibold uppercase tracking-wider min-h-[44px] inline-flex items-center gap-1.5" data-testid="service-slot-add">
                <Plus className="h-4 w-4" /> Add slot
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        <div className="admin-overline">Days it can be booked</div>
        <div className="flex flex-wrap gap-2 mt-2" data-testid="service-schedule-days">
          {DAY_LABELS.map((label, d) => {
            const on = value.days.includes(d);
            return (
              <button key={label} type="button" onClick={() => toggleDay(d)} aria-pressed={on}
                className={`px-3 text-xs font-semibold min-h-[44px] min-w-[48px] ${on ? "bg-black text-white" : "bg-neutral-100 text-neutral-500"}`} data-testid={`service-schedule-day-${d}`}>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="min-w-0"><label className="admin-overline" htmlFor="sched-from">Bookable from (date)</label>
          <input id="sched-from" type="date" value={value.available_from || ""} onChange={(e) => set({ available_from: e.target.value || null })} className={inputClass} data-testid="service-schedule-from" />
          <div className="text-[11px] text-neutral-500 mt-1">Blank: from today.</div></div>
        <div className="min-w-0"><label className="admin-overline" htmlFor="sched-until">Bookable until (date)</label>
          <input id="sched-until" type="date" min={value.available_from || undefined} value={value.available_until || ""} onChange={(e) => set({ available_until: e.target.value || null })} className={inputClass} data-testid="service-schedule-until" />
          <div className="text-[11px] text-neutral-500 mt-1">Blank: no end date.</div></div>
        <div className="min-w-0"><label className="admin-overline" htmlFor="sched-capacity">Bookings per slot</label>
          <input id="sched-capacity" type="number" min="1" max="500" step="1" placeholder="Automatic" value={value.capacity ?? ""}
            onChange={(e) => set({ capacity: e.target.value === "" ? null : parseInt(e.target.value, 10) })} className={inputClass} data-testid="service-schedule-capacity" />
          <div className="text-[11px] text-neutral-500 mt-1">Blank: one per assigned seller, or one when none is assigned.</div></div>
      </div>
    </div>
  );
}
